import { i18n } from "../../translate/i18n";
import React, { useEffect, useReducer, useState, useContext } from "react";
import { alpha, makeStyles, useTheme } from "@material-ui/core/styles";
import { purple } from "@material-ui/core/colors";
import toastError from "../../errors/toastError";
import Popover from "@material-ui/core/Popover";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import Notifications from "@material-ui/icons/Notifications";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import useNotificationSound from "../../hooks/useNotificationSound";
import alertSound from "../../assets/sound.mp3";

import {
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  Paper,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from "@material-ui/core";
import api from "../../services/api";
import { isArray } from "lodash";
import moment from "moment";
import { SocketContext } from "../../context/Socket/SocketContext";
import { isDarkTheme } from "../../helpers/darkTint";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    maxHeight: 3000,
    maxWidth: 5000,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

function AnnouncementDialog({ announcement, open, handleClose }) {
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const getMediaPath = (filename) => {
    return `${process.env.REACT_APP_BACKEND_URL}/public/${filename}`;
  };
  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{announcement.title}</DialogTitle>
      <DialogContent>
        {announcement.mediaPath && (
          <div
            style={{
              border: `1px solid ${isDark ? theme.palette.divider : "#f1f1f1"}`,
              margin: "0 auto 20px",
              textAlign: "center",
              width: "400px",
              height: 300,
              backgroundImage: `url(${getMediaPath(announcement.mediaPath)})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              backgroundPosition: "center",
            }}
          ></div>
        )}
        <DialogContentText id="alert-dialog-description">
          {announcement.text}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} color="primary" autoFocus>
          {i18n.t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

export default function AnnouncementsPopover() {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const play = useNotificationSound(alertSound, localStorage.getItem("volume"));

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(false);
  const [announcement, setAnnouncement] = useState({});
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  
  // Estado para notificações de indicação
  const [indicacoes, setIndicacoes] = useState([]);

  const socketManager = useContext(SocketContext);
  const [readAnnouncements, setReadAnnouncements] = useState([]); // Para armazenar IDs de anúncios lidos

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId"); // Obter userId do localStorage

    // Buscar anúncios lidos quando o componente montar ou userId mudar
    if (userId) {
      fetchReadAnnouncements(userId);
    }
    
    // Buscar indicações não lidas do banco (apenas para admins)
    if (user.profile === "admin") {
      fetchIndicacoesFromDB();
    }
    
    const socket = socketManager.getSocket(companyId);
    
    if (!socket) {
      return () => {}; // Retorna uma função de cleanup vazia se não houver socket
    }

    const handleCompanyAnnouncement = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
        setInvisible(false);
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    };

    socket.on(`company-announcement`, handleCompanyAnnouncement);
    
    // Listener para notificações de indicação (apenas para admins)
    const handleIndicacaoNotification = (data) => {
      if (data.action === "create" && user.profile === "admin") {
        console.log("[Indicação] Nova notificação recebida no sino:", data);
        
        const indicacaoNotification = {
          id: `indicacao-${data.data.id}`, // Usar ID do banco
          dbId: data.data.id, // ID do banco para marcar como lida
          type: "indicacao",
          title: data.data.clienteNome,
          text: i18n.t("announcementsPopover.referralText", { name: data.data.indicadoPorNome }),
          clienteId: data.data.clienteId,
          createdAt: new Date().toISOString(),
          priority: 1, // Alta prioridade (borda vermelha)
          isIndicacao: true
        };
        
        setIndicacoes(prev => {
          // Verificar se já existe
          const exists = prev.find(ind => ind.id === indicacaoNotification.id);
          if (exists) return prev;
          return [indicacaoNotification, ...prev];
        });
        
        // Tocar som
        play();
        
        // Mostrar badge
        setInvisible(false);
      }
    };

    socket.on(`company-${companyId}-indicacao`, handleIndicacaoNotification);
    
    return () => {
      // Limpar o listener e desconectar o socket ao desmontar
      socket.off(`company-announcement`, handleCompanyAnnouncement);
      socket.off(`company-${companyId}-indicacao`, handleIndicacaoNotification);
      socket.disconnect();
    };
    // A dependência [socketManager] significa que este efeito será re-executado
    // se a instância do socketManager mudar. Se o userId mudar (login/logout)
    // e o componente for remontado, o userId correto será pego do localStorage.
    // Se o componente não for remontado mas o userId mudar, e socketManager for estável,
    // seria ideal ter userId (de um estado ou contexto) como dependência.
    // Por ora, esta correção garante que fetchReadAnnouncements seja chamado.
  }, [socketManager]);

  const fetchReadAnnouncements = async (userId) => {
    try {
      const { data } = await api.get(`/users-announcements/read-by-user/${userId}`);
      setReadAnnouncements(data || []);
    } catch (err) {
      // Não precisa notificar o usuário sobre isso, mas pode logar o erro
      console.error("Failed to fetch read announcements", err);
    }
  };

  const fetchIndicacoesFromDB = async () => {
    try {
      const { data } = await api.get("/indicacao-notifications");
      
      if (data && isArray(data.indicacoes) && data.indicacoes.length > 0) {
        const formattedIndicacoes = data.indicacoes.map(ind => ({
          id: `indicacao-${ind.id}`,
          dbId: ind.id, // ID do banco para marcar como lida
          type: "indicacao",
          title: ind.clienteNome,
          text: i18n.t("announcementsPopover.referralText", { name: ind.indicadoPorNome }),
          clienteId: ind.clienteId,
          createdAt: ind.createdAt,
          priority: 1,
          isIndicacao: true
        }));
        
        setIndicacoes(formattedIndicacoes);
        setInvisible(false); // Mostrar badge se houver indicações
      }
    } catch (err) {
      console.error("Failed to fetch indicações", err);
    }
  };

  const markIndicacaoAsRead = async (dbId) => {
    try {
      await api.post("/indicacao-notifications/mark-as-read", {
        indicacaoNotificationId: dbId
      });
      console.log("[Indicação] Marcada como lida:", dbId);
    } catch (err) {
      console.error("Failed to mark indicação as read", err);
      toastError(err);
    }
  };

  const markAnnouncementAsRead = async (announcementId) => {
    const userId = localStorage.getItem("userId");
    if (!userId) return; // Não fazer nada se não houver userId

    // Verifica se já não foi marcado como lido para evitar requisições desnecessárias
    if (readAnnouncements.includes(announcementId)) return;

    try {
      await api.post("/users-announcements/mark-as-read", {
        userId: parseInt(userId),
        announcementId: announcementId,
      });
      // Adiciona o ID à lista local para refletir a mudança na UI imediatamente
      setReadAnnouncements(prev => [...prev, announcementId]);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = async (event) => {
    setAnchorEl(event.currentTarget);
    // Marcar todos os anúncios atuais como lidos
    try {
      const userId = localStorage.getItem("userId");
      if (userId) {
        const markAsReadPromises = announcements
          .filter(ann => !readAnnouncements.includes(ann.id)) // Marcar apenas os não lidos
          .map(ann => 
            api.post("/users-announcements/mark-as-read", {
              userId: parseInt(userId),
              announcementId: ann.id,
            })
          );
        
        await Promise.all(markAsReadPromises);
        // Atualizar o estado local de readAnnouncements para refletir as mudanças
        const newlyReadIds = announcements
          .filter(ann => !readAnnouncements.includes(ann.id))
          .map(ann => ann.id);
        setReadAnnouncements(prev => [...prev, ...newlyReadIds]);
      }
    } catch (err) {
      toastError(err); // Tratar possíveis erros ao marcar como lido
    }
    setInvisible(true); // Isso pode ser reavaliado se o badge deve sumir imediatamente
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleIndicacaoClick = async (indicacao) => {
    // Marcar como lida no banco de dados
    if (indicacao.dbId) {
      await markIndicacaoAsRead(indicacao.dbId);
    }
    
    // Remover a indicação da lista ao clicar
    setIndicacoes(prev => prev.filter(ind => ind.id !== indicacao.id));
    
    // Fechar popover
    setAnchorEl(null);
    
    // Redirecionar para o cliente
    history.push(`/clientes/${indicacao.clienteId}`);
  };

  const borderPriority = (priority) => {
    if (priority === 1) {
      return "4px solid #b81111";
    }
    if (priority === 2) {
      return "4px solid orange";
    }
    if (priority === 3) {
      return "4px solid grey";
    }
  };

  const getMediaPath = (filename) => {
    return `${process.env.REACT_APP_BACKEND_URL}/public/${filename}`;
  };

  const handleShowAnnouncementDialog = (record) => {
    setAnnouncement(record);
    setShowAnnouncementDialog(true);
    setAnchorEl(null);
    // Marcar como lido quando o diálogo do anúncio é aberto
    markAnnouncementAsRead(record.id);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <AnnouncementDialog
        announcement={announcement}
        open={showAnnouncementDialog}
        handleClose={() => setShowAnnouncementDialog(false)}
      />
      <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.announcements")}>
        <IconButton
          variant="contained"
          aria-describedby={id}
          aria-label={i18n.t("mainDrawer.appBar.tooltips.announcements")}
          onClick={handleClick}
          style={{ color: "white" }}
        >
          <Badge
            color="secondary"
            variant="dot"
            invisible={
              invisible || 
              (announcements.every(ann => readAnnouncements.includes(ann.id)) && indicacoes.length === 0)
            }
          >
            <Notifications />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Paper
          variant="outlined"
          onScroll={handleScroll}
          className={classes.mainPaper}
        >
          <List
            component="nav"
            aria-label={i18n.t("mainDrawer.appBar.tooltips.announcements")}
            style={{ minWidth: 300 }}
          >
            {/* Mostrar indicações primeiro (apenas para admins) */}
            {user.profile === "admin" && isArray(indicacoes) && indicacoes.length > 0 && (
              <>
                {indicacoes.map((item, key) => (
                  <ListItem
                    key={`indicacao-${item.id}`}
                    style={{
                      border: `1px solid ${isDark ? theme.palette.divider : "#eee"}`,
                      borderLeft: "4px solid #9c27b0", // Roxo para indicações
                      cursor: "pointer",
                      backgroundColor: isDark ? alpha(purple[500], 0.2) : "#f3e5f5", // Fundo roxo claro
                    }}
                    onClick={() => handleIndicacaoClick(item)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" style={{ fontWeight: "bold" }}>
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" style={{ fontSize: 12 }}>
                            {item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY HH:mm") : i18n.t("announcementsPopover.dateUnavailable")}
                          </Typography>
                          <span style={{ marginTop: 5, display: "block" }}></span>
                          <Typography component="span" variant="body2">
                            {item.text}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
                {/* Divisor entre indicações e anúncios */}
                {isArray(announcements) && announcements.length > 0 && (
                  <ListItem style={{ backgroundColor: isDark ? theme.palette.action.hover : "#f5f5f5", padding: "4px 16px" }}>
                    <ListItemText 
                      primary={
                        <Typography variant="caption" style={{ fontWeight: "bold", color: isDark ? theme.palette.text.secondary : "#666" }}>
                          {i18n.t("announcementsPopover.announcementsDivider")}
                        </Typography>
                      } 
                    />
                  </ListItem>
                )}
              </>
            )}
            
            {/* Anúncios normais */}
            {isArray(announcements) &&
              announcements.map((item, key) => (
                <ListItem
                  key={key}
                  style={{
                    //background: key % 2 === 0 ? "#ededed" : "white",
                    border: `1px solid ${isDark ? theme.palette.divider : "#eee"}`,
                    borderLeft: borderPriority(item.priority),
                    cursor: "pointer",
                    // Adicionar um estilo visual para anúncios lidos (opcional)
                    opacity: readAnnouncements.includes(item.id) ? 0.6 : 1,
                  }}
                  onClick={() => handleShowAnnouncementDialog(item)}
                >
                  {item.mediaPath && (
                    <ListItemAvatar>
                      <Avatar
                        alt={item.mediaName}
                        src={getMediaPath(item.mediaPath)}
                      />
                    </ListItemAvatar>
                  )}
                  <ListItemText
                    primary={item.title}
                    secondary={
                      <>
                        <Typography component="span" style={{ fontSize: 12 }}>
                          {item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : i18n.t("announcementsPopover.dateUnavailable")}
                        </Typography>
                        <span style={{ marginTop: 5, display: "block" }}></span>
                        <Typography component="span" variant="body2">
                          {item.text}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            {isArray(announcements) && announcements.length === 0 && indicacoes.length === 0 && (
              <ListItemText primary={i18n.t("common.noRecords")} />
            )}
          </List>
        </Paper>
      </Popover>
    </div>
  );
}
